import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AuthService } from '@auth0/auth0-angular';
import { environment } from '../../environments/environment';
import { Observable, firstValueFrom, shareReplay } from 'rxjs';
import { StreamEvent } from '../types';

export interface DebugEvent {
  timestamp: string;
  node: string;
  data: unknown;
}

export interface DebugDoc {
  _id: string;
  meta: Record<string, unknown>;
  events: DebugEvent[];
}

export interface QueryResponse {
  message: any;
  conversation_id?: string;
  debug_id?: string | null;
}

export interface MarineSurveyTaskSummary {
  id: string;
  name: string;
}

export type MarineSurveyTasksByStatus = Record<string, MarineSurveyTaskSummary[]>;

export interface ConversationMessagesResponse {
  messages: Array<{
    role: string;
    content: string;
  }>;
}

export interface EnsureUserRequest {
  sub?: string;
  email?: string;
  email_verified?: boolean;
  name?: string;
  picture?: string;
}

export interface EnsureUserResponse {
  ok: boolean;
}

@Injectable({ providedIn: 'root' })
export class ApiService {
  private baseUrl = environment.apiBaseUrl;
  private workflowBaseUrl = environment.workflowApiBaseUrl;

  private debugDocCache = new Map<string, Observable<DebugDoc>>();

  constructor(
    private http: HttpClient,
    private auth: AuthService,
  ) {}

  query(message: string, conversationId?: string | null): Observable<QueryResponse> {
    const body: any = {
      message,
    };
    if (conversationId) body.conversation_id = conversationId;

    return this.http.post<QueryResponse>(
      `${this.workflowBaseUrl}/marine-survey/chat/message/stream`,
      body,
      { headers: { 'Content-Type': 'application/json' } },
    );
  }

  async stream(
    message: string,
    conversationId: string | null | undefined,
    onEvent: (event: StreamEvent) => void,
  ): Promise<{ conversation_id: string | null }> {
    const body: any = { content:message };
    if (conversationId) body.conversation_id = conversationId;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    try {
      const token = await firstValueFrom(
        this.auth.getAccessTokenSilently({
          authorizationParams: {
            audience: environment.auth0.audience,
          },
        }),
      );
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    } catch (error) {
      // Keep parity with HTTP interceptor behavior: proceed even if token fetch fails.
      console.error('Error getting access token for stream', error);
    }

    const response = await fetch(`${this.workflowBaseUrl}/marine-survey/chat/message/stream`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const text = await response.text();
      throw new Error(`Stream request failed (${response.status}): ${text || response.statusText}`);
    }

    let resolvedConversationId: string | null = null;

    if (!response.body) {
      throw new Error('Stream response has no body.');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let eventData: string[] = [];

    const emitEvent = () => {
      if (!eventData.length) return;
      const raw = eventData.join('\n');
      eventData = [];
      try {
        const parsed = JSON.parse(raw) as StreamEvent;
        if (
          parsed.type === 'session'
          && parsed.data
          && typeof parsed.data.conversation_id === 'string'
          && parsed.data.conversation_id.trim()
        ) {
          resolvedConversationId = parsed.data.conversation_id;
        }
        onEvent(parsed);
      } catch {
        // Ignore malformed SSE payloads.
      }
    };

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() ?? '';

      for (const rawLine of lines) {
        const line = rawLine.replace(/\r$/, '');
        if (!line) {
          emitEvent();
          continue;
        }
        if (line.startsWith('data:')) {
          eventData.push(line.slice(5).trimStart());
        }
      }
    }

    buffer += decoder.decode();
    if (buffer.trim()) {
      const line = buffer.replace(/\r$/, '');
      if (line.startsWith('data:')) {
        eventData.push(line.slice(5).trimStart());
      }
    }
    emitEvent();

    return { conversation_id: resolvedConversationId };
  }

  getMarineSurveyTasks(): Observable<MarineSurveyTasksByStatus> {
    return this.http.post<MarineSurveyTasksByStatus>(
      `${this.workflowBaseUrl}/marine-survey/tasks`,
      { 
        statuses: [
          'INQUIRY_CREATED', 
          // 'FORM_SENT', 
          // 'WAITING_FOR_FORM', 
          // 'FORM_COMPLETED', 
          // 'NEEDS_SCHEDULING', 
          // 'SCHEDULED', 
          // 'TIMED_OUT', 
          // 'SCHEDULING_TIMED_OUT'
        ] 
      },
      { headers: { 'Content-Type': 'application/json' } },
    );
  }

  getMarineSurveyMessages(conversationId: string): Observable<ConversationMessagesResponse> {
    return this.http.post<ConversationMessagesResponse>(
      `${this.workflowBaseUrl}/marine-survey/chat/messages`,
      {  conversation_id: conversationId },
      { headers: { 'Content-Type': 'application/json' } },
    );
  }

  ensureUser(body: EnsureUserRequest): Observable<EnsureUserResponse> {
    return this.http.post<EnsureUserResponse>(
      `${this.baseUrl}/api/v1/users/ensure`,
      body,
      { headers: { 'Content-Type': 'application/json' } },
    );
  }

  getDebug(debugId: string): Observable<DebugDoc> {
    return this.http.get<DebugDoc>(`${this.baseUrl}/api/v1/debug/${debugId}`);
  }

  /**
   * Cached version of getDebug. Navigating away/back to the same debugId
   * won't refetch unless you request a different debugId.
   */
  getDebugCached(debugId: string): Observable<DebugDoc> {
    const existing = this.debugDocCache.get(debugId);
    if (existing) return existing;

    const req$ = this.getDebug(debugId).pipe(
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    this.debugDocCache.set(debugId, req$);
    return req$;
  }

  /**
   * Force-refresh the cached debug document for a given debugId.
   * Useful after a new query completes so the debug screen has fresh data.
   */
  refreshDebugCached(debugId: string): Observable<DebugDoc> {
    const req$ = this.getDebug(debugId).pipe(
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    this.debugDocCache.set(debugId, req$);
    return req$;
  }
}

